import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { Palette, Save, RotateCcw, FileText, Eye } from 'lucide-react';
import {
  PdfBrandingConfig,
  defaultBrandingConfig,
  getBrandingConfig,
  saveBrandingConfig,
  exportAnalysisToPDF,
} from '@/lib/pdfExport';

export function KompaktBrandingEditor() {
  const [config, setConfig] = useState<PdfBrandingConfig>(defaultBrandingConfig);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setConfig(getBrandingConfig());
  }, []);

  const handleChange = (key: keyof PdfBrandingConfig, value: string | number) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveBrandingConfig(config);
    setHasChanges(false);
    toast({
      title: 'Branding gespeichert',
      description: 'Die PDF-Einstellungen wurden gespeichert.',
    });
  };

  const handleReset = () => {
    setConfig(defaultBrandingConfig);
    saveBrandingConfig(defaultBrandingConfig);
    setHasChanges(false);
    toast({
      title: 'Zurückgesetzt',
      description: 'Die Standard-Einstellungen wurden wiederhergestellt.',
    });
  };

  const handlePreview = async () => {
    // Save current config first
    saveBrandingConfig(config);
    
    // Generate a sample PDF
    await exportAnalysisToPDF({
      fileName: 'Beispiel-Dokument.pdf',
      summary: 'Dies ist eine Vorschau des PDF-Exports mit Ihren aktuellen Branding-Einstellungen. Die Zusammenfassung zeigt, wie längerer Text im PDF dargestellt wird.',
      keywords: ['Vorschau', 'Branding', 'Test', 'Kompakt'],
      sentiment: {
        overall: 'positiv',
        positive: 75,
        neutral: 20,
        negative: 5,
      },
      themes: [
        { theme: 'PDF-Gestaltung', sentiment: 'positiv' },
        { theme: 'Branding-Anpassung', sentiment: 'neutral' },
        { theme: 'Benutzerfreundlichkeit', sentiment: 'positiv' },
      ],
      createdAt: new Date(),
    });

    toast({
      title: 'Vorschau generiert',
      description: 'Eine Beispiel-PDF wurde heruntergeladen.',
    });
  };

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="text-xl flex items-center gap-2">
          <Palette className="w-5 h-5" />
          PDF-Branding Einstellungen
        </CardTitle>
        <CardDescription>
          Passen Sie das Erscheinungsbild der exportierten PDF-Dokumente an
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Logo Size */}
          <div className="space-y-3">
            <Label htmlFor="logoHeight">Logo-Größe (Höhe in mm)</Label>
            <div className="flex items-center gap-4">
              <Slider
                id="logoHeight"
                min={16}
                max={32}
                step={1}
                value={[config.logoHeight]}
                onValueChange={([value]) => handleChange('logoHeight', value)}
                className="flex-1"
              />
              <span className="w-12 text-right font-mono text-sm">{config.logoHeight}mm</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Die Breite wird automatisch basierend auf dem Seitenverhältnis berechnet.
            </p>
          </div>

          {/* Header Title */}
          <div className="space-y-3">
            <Label htmlFor="headerTitle">Header-Titel (neben Logo)</Label>
            <Input
              id="headerTitle"
              value={config.headerTitle}
              onChange={(e) => handleChange('headerTitle', e.target.value)}
              placeholder="z.B. Kompakt"
              maxLength={30}
            />
            <p className="text-xs text-muted-foreground">
              Wird neben dem Logo im PDF-Header angezeigt.
            </p>
          </div>

          {/* Slogan */}
          <div className="space-y-3 md:col-span-2">
            <Label htmlFor="slogan">Footer-Slogan</Label>
            <Input
              id="slogan"
              value={config.slogan}
              onChange={(e) => handleChange('slogan', e.target.value)}
              placeholder="z.B. Keine komplizierte Fachsprache. Keine versteckten Kosten."
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              Erscheint in der Fußzeile jeder PDF-Seite.
            </p>
          </div>

          {/* Website URL */}
          <div className="space-y-3 md:col-span-2">
            <Label htmlFor="websiteUrl">Website-URL</Label>
            <Input
              id="websiteUrl"
              value={config.websiteUrl}
              onChange={(e) => handleChange('websiteUrl', e.target.value)}
              placeholder="https://klartech-mueritz.de"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Wird unter dem Slogan in der Fußzeile angezeigt.
            </p>
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-8 p-4 bg-muted/50 rounded-lg border">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Vorschau
          </h4>
          <div className="bg-white border rounded shadow-sm overflow-hidden">
            {/* Header Preview */}
            <div className="bg-cyan-600 p-3 flex items-center gap-3">
              <div 
                className="bg-white/20 rounded flex items-center justify-center text-white text-xs"
                style={{ 
                  width: `${config.logoHeight * 1.5}px`, 
                  height: `${config.logoHeight}px` 
                }}
              >
                LOGO
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">{config.headerTitle || 'Kompakt'}</div>
                <div className="text-sm opacity-90">Dokumentenanalyse</div>
              </div>
            </div>
            {/* Content Preview */}
            <div className="p-3 text-xs text-muted-foreground">
              <div className="h-12 flex items-center">PDF-Inhalt...</div>
            </div>
            {/* Footer Preview */}
            <div className="border-t p-2 text-center text-[10px] text-muted-foreground">
              <div>Klartech Müritz – {config.slogan || 'Ihr Slogan'}</div>
              <div>{config.websiteUrl || 'https://...'}</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 mt-6">
          <Button onClick={handleSave} disabled={!hasChanges}>
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="w-4 h-4 mr-2" />
            PDF-Vorschau
          </Button>
          <Button variant="ghost" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Zurücksetzen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
